import {
  Alert,
  Button,
  Divider,
  FormAlert,
  Label,
  LabelGroup,
  Modal,
  ModalVariant,
  Form,
  FormGroup,
  Flex,
  FlexItem,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { RequireFeatureFlag } from '@app/components/feature_flags/require-feature-flag'
import { ReadOnlyPatientCard } from '@app/components/patient/patient-card/read-only-patient-card'
import { ProgramInteractionSelect } from '@app/components/program/program-interaction-select'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import { displayLabelIfExists } from '@app/domain/instrument/instrument.helpers'
import { useProgramInteractionHistory } from '@app/domain/program/program.hooks'
import type {
  IProgram,
  IProgramInteractionId,
} from '@app/domain/program/program.interface'
import { getVisitIntakeStartActionLabelPresentTense } from '@app/domain/visit/visit.helpers'
import {
  useGetVisitDefinition,
  useGetVisitPrograms,
} from '@app/domain/visit/visit.hooks'
import type {
  IVisit} from '@app/domain/visit/visit.interface';
import {
  NotStartedVisitStatusIds,
  VisitStatusId,
} from '@app/domain/visit/visit.interface'
import type { IApiVisitDefinition } from '@app/state/visits/helpers'

import { ReadOnlyVisitSummaryCard } from '../visit-card/read-only-visit-card'

const NUMBER_OF_LABELS_BEFORE_HIDING = 10

interface IProgramInteractionSelectionProps {
  onSelected: (instrumentIds: IProgramInteractionId[]) => void
  program: IProgram
  programInteractionHistory: Record<IProgramInteractionId, Date>
  selectedInteractions: IProgramInteractionId[]
  visit: IVisit
}

export const ProgramInteractionSelection = ({
  onSelected,
  program,
  programInteractionHistory,
  selectedInteractions,
  visit,
}: IProgramInteractionSelectionProps) => {
  if (Object.values(program.interactions).length > 0) {
    const programSelectIsDisabled = !NotStartedVisitStatusIds.includes(
      visit?.status,
    )
    return (
      <FormGroup
        label={`Program "${program.name}":`}
        hasNoPaddingTop
        fieldId={program.programId}
      >
        <ProgramInteractionSelect
          isDisabled={programSelectIsDisabled}
          program={program}
          onChange={onSelected}
          selectedInteractionIds={selectedInteractions}
          programInteractionHistory={programInteractionHistory}
        />
      </FormGroup>
    )
  } else {
    return null
  }
}

const InstrumentListHeader = styled.div`
  padding-top: 0.5em;
`

const InstrumentList = styled.div`
  padding-top: 0.5em;
  padding-bottom: 1em;
  font-weight: bold;
`

export interface Props {
  errorMessage: string
  isOpen?: boolean
  onClose?: () => void
  onStartIntake: (visit: IVisit, selectedInteractions: string[]) => void
  onStartInteraction?: (visit: IVisit, selectedInteractions: string[]) => void
  visit?: IVisit
}

export const LaunchIntakeModal = ({
  errorMessage,
  isOpen,
  onClose,
  onStartIntake,
  onStartInteraction,
  visit,
}: Props) => {
  const { visitDefinition } = useGetVisitDefinition(visit?.id)
  const { programs, getVisitPrograms } = useGetVisitPrograms(visit?.id)

  const {
    programInteractionHistory,
    getProgramInteractionHistory,
  } = useProgramInteractionHistory()

  const preparedVisitStartActionLabel = useMemo(() => {
    return getVisitIntakeStartActionLabelPresentTense(visit?.status)
  }, [visit])

  useEffect(() => {
    if (visit?.patient?.id) {
      getProgramInteractionHistory(visit.patient.id)
    }
  }, [getProgramInteractionHistory, visit])

  const [selectedInteractions, setSelectedInteractions] = useState<
    IProgramInteractionId[]
  >([])

  useEffect(() => {
    getVisitPrograms()
  }, [getVisitPrograms])

  useEffect(() => {
    const savedInteractions = visitDefinition?.programInteractions.map(
      (interaction) => interaction.id,
    )
    setSelectedInteractions(savedInteractions)
  }, [programs, visitDefinition])

  const getSelectedInstrumentsFromProgram = useCallback(
    (program: IProgram, selectedInteractions: IProgramInteractionId[]) => {
      return Object.values(program.interactions)
        .flatMap((i) => i)
        .filter((i) => selectedInteractions?.includes(i.id))
        .flatMap((i) => i.instruments)
        .map(displayLabelIfExists)
    },
    [],
  )

  const getVisitInstruments = useCallback(
    (visitDefinition: IApiVisitDefinition) => {
      if (visitDefinition?.visitInstruments?.length > 0) {
        return visitDefinition.visitInstruments.map(displayLabelIfExists)
      } else {
        return []
      }
    },
    [],
  )

  const combineVisitAndSelectedInstruments = (
    visitInstruments: string[],
    selectedInstruments: string[],
  ) => {
    return Array.from(new Set(visitInstruments?.concat(selectedInstruments)))
  }

  const combinedInstruments = useMemo(() => {
    const visitInstruments = getVisitInstruments(visitDefinition)
    const selectedInstruments = programs.flatMap((program) =>
      getSelectedInstrumentsFromProgram(program, selectedInteractions),
    )
    return combineVisitAndSelectedInstruments(
      visitInstruments,
      selectedInstruments,
    )
  }, [
    getSelectedInstrumentsFromProgram,
    getVisitInstruments,
    programs,
    selectedInteractions,
    visitDefinition,
  ])

  const handleOnConfirm = useCallback(() => {
    onStartInteraction(visit, selectedInteractions)
  }, [onStartInteraction, selectedInteractions, visit])

  const handleOnConfirmAndLaunch = useCallback(() => {
    onStartIntake(visit, selectedInteractions)
  }, [onStartIntake, selectedInteractions, visit])

  const handleProgramInteractionSelection = useCallback(
    (interactionIds: IProgramInteractionId[]) => {
      setSelectedInteractions(interactionIds)
    },
    [],
  )

  const preparedActionButtons = React.useMemo(() => {
    const actions = []

    actions.push(
      <Button
        key="confirm-and-launch"
        variant="primary"
        onClick={handleOnConfirmAndLaunch}
      >
        Confirm and Launch Intake
      </Button>,
    )

    if (
      (onStartInteraction &&
        visit &&
        visit?.status === VisitStatusId.NotStarted) ||
      visit?.status === VisitStatusId.Ordered
    ) {
      actions.push(
        <Button key="confirm" variant="primary" onClick={handleOnConfirm}>
          Confirm
        </Button>,
      )
    }

    actions.push(
      <Button key="cancel" variant="secondary" onClick={onClose}>
        Cancel
      </Button>,
    )

    return actions
  }, [
    handleOnConfirm,
    handleOnConfirmAndLaunch,
    onClose,
    onStartInteraction,
    visit,
  ])

  return (
    <Modal
      variant={ModalVariant.medium}
      title={preparedVisitStartActionLabel}
      isOpen={isOpen}
      onClose={onClose}
      actions={preparedActionButtons}
    >
      <Flex spaceItems={{ default: 'spaceItems2xl' }}>
        <FlexItem>
          {visit ? (
            <ReadOnlyVisitSummaryCard isVisitUpdated={false} visit={visit} />
          ) : null}
        </FlexItem>
        <Divider isVertical={true} inset={{ default: 'insetMd' }} />
        <FlexItem>
          {visit?.patient ? (
            <ReadOnlyPatientCard
              isSelectPatientAllowed={false}
              patient={visit.patient}
            />
          ) : null}
        </FlexItem>
      </Flex>
      <RequireFeatureFlag
        key="add_program_interaction_to_intake_enabled"
        feature={FeatureFlagId.AddProgramToIntake}
      >
        <Divider />
        <InstrumentListHeader>
          Instruments from Visit Type:
        </InstrumentListHeader>
        {errorMessage ? (
          <FormAlert>
            <Alert
              variant="danger"
              title={errorMessage}
              aria-live="polite"
              isInline
            />
          </FormAlert>
        ) : null}

        <InstrumentList>
          <LabelGroup numLabels={NUMBER_OF_LABELS_BEFORE_HIDING}>
            {visitDefinition?.visitInstruments
              ?.map(displayLabelIfExists)
              .map((label, index) => {
                return <Label key={index}>{label}</Label>
              })}
          </LabelGroup>
        </InstrumentList>
        {programs?.length > 0 ? (
          <>
            <Form>
              {programInteractionHistory
                ? programs?.map((program) => {
                    return (
                      <ProgramInteractionSelection
                        key={program.programId}
                        visit={visit}
                        program={program}
                        onSelected={handleProgramInteractionSelection}
                        selectedInteractions={selectedInteractions}
                        programInteractionHistory={programInteractionHistory}
                      />
                    )
                  })
                : null}
            </Form>
            <InstrumentListHeader>
              Total Instruments for Survey:
            </InstrumentListHeader>
            <InstrumentList>
              <LabelGroup numLabels={NUMBER_OF_LABELS_BEFORE_HIDING}>
                {combinedInstruments.map((label, index) => {
                  return <Label key={index}>{label}</Label>
                })}
              </LabelGroup>
            </InstrumentList>
          </>
        ) : null}
      </RequireFeatureFlag>
    </Modal>
  )
}
